import React, {
  CSSProperties,
  FormEventHandler,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  faCheckCircle as CircleIcon,
  faExclamationCircle,
  faPencil
} from '@fortawesome/pro-light-svg-icons';
import {
  faCheckCircle as SolidCircleIcon,
  faCircleX as XIcon
} from '@fortawesome/pro-solid-svg-icons';
import { path } from 'ramda';
import { FormProvider, UseFormReturn } from 'react-hook-form';

import { DARK } from 'constants/colors';

import { Box, IconButton, styled, Typography } from '@mui/material';

import DrawerFooter from '../Drawer/DrawerFooter';
import FontAwesomeSvgIcon from '../FontAwesomeSvgIcon';
import { MultiStepFormContext, useMultiStepFormContext } from './context';
import { makeStyles } from '@material-ui/core';
import { DrawerContext } from '../Drawer/DrawerContext';

const useStyles = makeStyles(() => ({
  fixedHeight: {
    maxHeight: 'calc(100vh - 158px)',
    overflowY: 'auto',
    padding: '0 20px'
  },
  autoHeight: {
    padding: 0
  }
}));

const StyledSection = styled('section')<{ fullPadding: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  padding: ${(props) => (props.fullPadding ? '30px 0' : '0 0 50px 0')};
  border-bottom: 1px solid #e0e0e0;

  &:last-of-type {
    border: none;
  }
`;

function MultiStepFormSectionPreview({
  children,
  preview,
  isPreview,
  isEditable
}: {
  children: React.ReactNode;
  preview: React.ReactNode;
  isPreview: boolean;
  isEditable: boolean;
}) {
  const {
    formState: { submitCount }
  } = useMultiStepFormContext();

  const [editing, setEditing] = useState<boolean>(isPreview);

  useEffect(() => {
    setEditing(false);
  }, [submitCount]);

  function onEditClick() {
    setEditing(!editing);
  }

  return (
    <>
      {!editing ? (
        <Box
          display="flex"
          width="300px"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Box sx={{ fontSize: '16px' }}>{preview}</Box>
          {isEditable && (
            <IconButton onClick={onEditClick}>
              <FontAwesomeSvgIcon
                icon={faPencil}
                sx={{
                  color: DARK
                }}
              />
            </IconButton>
          )}
        </Box>
      ) : (
        children
      )}
    </>
  );
}

enum Status {
  Pending = 'disabled',
  Success = 'primary',
  Error = 'error',
  Warning = 'warning'
}

type MultiStepFormSectionProps = {
  title: string;
  children?: React.ReactNode;
  preview?: React.ReactNode;
  validateFields?: string[];
  isEditable?: boolean;
  isPreview?: boolean;
  fullWidth?: boolean;
  fullPadding?: boolean;
  isImportant?: boolean;
};

function MultiStepFormSection({
  children,
  title,
  validateFields = [],
  preview,
  isPreview = false,
  isEditable = true,
  isImportant = false,
  fullWidth = false,
  fullPadding = true
}: MultiStepFormSectionProps) {
  const {
    previewMode,
    formState: { errors },
    getFieldState,
    watch,
    getValues
  } = useMultiStepFormContext();

  const [status, setStatus] = useState<Status>(Status.Pending);

  useEffect(() => {
    if (
      validateFields.every((field) => {
        const fieldState = getFieldState(field);
        const value = getValues(field);

        // Check if user has entered a value and if the value is valid
        return Boolean(value) && !fieldState.invalid;
      })
    ) {
      setStatus(Status.Success);
      return;
    }

    if (validateFields.some((field) => path(field?.split('.'), errors))) {
      setStatus(Status.Error);
      return;
    }

    setStatus(Status.Pending);
  }, [watch()]);

  const showPreview = isPreview || previewMode;

  const selectedIcon = useMemo(
    () =>
      status === 'primary'
        ? SolidCircleIcon
        : status === 'disabled'
        ? CircleIcon
        : status === 'error'
        ? XIcon
        : CircleIcon,
    [status]
  );

  return (
    <StyledSection fullPadding={fullPadding}>
      <Box
        display="flex"
        alignItems="center"
        maxWidth={!fullWidth ? '300px' : '100vh'}
        gap={2}
      >
        <FontAwesomeSvgIcon
          icon={isImportant ? faExclamationCircle : selectedIcon}
          color={isImportant ? Status.Warning : status}
        />
        <Typography variant={'h3'}>{title}</Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        gap={2.5}
        marginLeft={5}
        marginTop={2}
      >
        {!showPreview && isEditable ? (
          children
        ) : (
          <MultiStepFormSectionPreview
            preview={preview}
            isPreview={isPreview}
            isEditable={isEditable}
          >
            {children}
          </MultiStepFormSectionPreview>
        )}
      </Box>
    </StyledSection>
  );
}

type Props = {
  children: React.ReactNode;
  methods: UseFormReturn<any>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  fixedHeight?: boolean;
  previewMode?: boolean;
  style?: CSSProperties;
};

function MultiStepForm({
  children,
  methods,
  onSubmit,
  fixedHeight = true,
  previewMode = false,
  style
}: Props) {
  const classes = useStyles();
  const { onInnerFormChange } = useContext(DrawerContext);

  useEffect(() => {
    onInnerFormChange?.(methods.formState);
  }, [methods.formState.isDirty]);

  return (
    <MultiStepFormContext.Provider value={{ previewMode }}>
      <FormProvider {...methods}>
        <form
          className={fixedHeight ? classes.fixedHeight : classes.autoHeight}
          onSubmit={methods.handleSubmit(onSubmit)}
          noValidate
          {...(style ? { style } : {})}
        >
          {children}
        </form>
      </FormProvider>
    </MultiStepFormContext.Provider>
  );
}

MultiStepForm.Section = MultiStepFormSection;
MultiStepForm.SectionPreview = MultiStepFormSectionPreview;
MultiStepForm.Actions = DrawerFooter;

export default MultiStepForm;
